import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { ToastContainer } from 'react-toastify';
import { ConfirmProvider } from './components/ConfirmDialog';
import 'react-toastify/dist/ReactToastify.css';

import LoginPage            from './pages/LoginPage';
import RegisterPage         from './pages/RegisterPage';
import ExamRegistration     from './pages/ExamRegistration';
import StudentDashboard     from './pages/StudentDashboard';
import AdminDashboard       from './pages/AdminDashboard';
import OrgDashboard         from './pages/OrgDashboard';
import InstructorDashboard  from './pages/InstructorDashboard';
import OptInPage            from './pages/OptInPage';
import PermissionCheckPage  from './pages/PermissionCheckPage';
import ExamPage             from './pages/ExamPage';
import ResultPage           from './pages/ResultPage';
import CreateExamPage from './pages/CreateExamPage';
import ManageExams          from './pages/ManageExams';
import ProctoringReview     from './pages/ProctoringReview';
import LiveMonitor          from './pages/LiveMonitor';
import AttendanceReport     from './pages/AttendanceReport';
import ExamResultReport     from './pages/ExamResultReport';
import QADownload           from './pages/QADownload';

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!user)   return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  return (
    <button className="theme-toggle" onClick={toggleTheme}
      title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
      {theme === 'dark' ? '☀️' : '🌙'}
    </button>
  );
}

// Each role gets its own distinct dashboard
function DashboardRouter() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  switch (user.role) {
    case 'admin':        return <AdminDashboard />;
    case 'organization': return <OrgDashboard />;
    case 'instructor':   return <InstructorDashboard />;
    case 'student':      return <StudentDashboard />;
    default:             return <Navigate to="/login" replace />;
  }
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  return (
    <Routes>
      {/* Public */}
      <Route path="/login"           element={user ? <Navigate to="/dashboard"/> : <LoginPage />} />
      <Route path="/register"        element={user ? <Navigate to="/dashboard"/> : <RegisterPage />} />
      <Route path="/register/:token" element={<ExamRegistration />} />

      {/* Role-based dashboard */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardRouter /></ProtectedRoute>} />

      {/* Student opt-in page (enrollment confirmation) */}
      <Route path="/opt-in"            element={<ProtectedRoute roles={['student']}><OptInPage /></ProtectedRoute>} />

      {/* Student only */}
      <Route path="/exam/check"        element={<ProtectedRoute roles={['student']}><PermissionCheckPage /></ProtectedRoute>} />
      <Route path="/exam/:examId"      element={<ProtectedRoute roles={['student']}><ExamPage /></ProtectedRoute>} />
      <Route path="/result/:attemptId" element={<ProtectedRoute><ResultPage /></ProtectedRoute>} />

      {/* Instructor + Org + Admin (read-only for admin) */}
      <Route path="/exams/create" element={<ProtectedRoute roles={['organization']}><CreateExamPage /></ProtectedRoute>} />
      <Route path="/manage/exams"       element={<ProtectedRoute roles={['admin','organization','instructor']}><ManageExams /></ProtectedRoute>} />
      <Route path="/proctor/:attemptId" element={<ProtectedRoute roles={['admin','organization','instructor']}><ProctoringReview /></ProtectedRoute>} />
      <Route path="/live/:examId"       element={<ProtectedRoute roles={['admin','organization','instructor']}><LiveMonitor /></ProtectedRoute>} />

      {/* Reports — attendance, results, Q&A download */}
      <Route path="/reports/attendance/:examId"          element={<ProtectedRoute roles={['admin','organization','instructor']}><AttendanceReport /></ProtectedRoute>} />
      <Route path="/reports/result/:examId/:attemptId"   element={<ProtectedRoute roles={['admin','organization','instructor']}><ExamResultReport /></ProtectedRoute>} />
      <Route path="/reports/qa/:examId"                  element={<ProtectedRoute roles={['organization']}><QADownload /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to={user ? "/dashboard" : "/login"} replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <ConfirmProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <ThemeToggle />
          <ToastContainer position="top-right" autoClose={4000} theme="colored" />
        </BrowserRouter>
      </AuthProvider>
      </ConfirmProvider>
    </ThemeProvider>
  );
}
